import {
  getAnalytics,
  isSupported,
  logEvent,
  type Analytics,
} from "firebase/analytics";
import type { SubmissionAnalytics } from "@ks-gen/shared";
import { getFirebaseApp, getFirebaseConfig } from "./firebase";

/**
 * Google Analytics 4 (Firebase Analytics) for the generator funnel.
 *
 * Reports to the GA4 property linked to the `generator-sales` Firebase project
 * (property 545116124, stream generator-sales-tool, measurement id G-T2FLWGKG8W).
 * The measurement id rides in FIREBASE_WEBAPP_CONFIG, injected at build by App
 * Hosting — nothing to hardcode. VITE_GA_MEASUREMENT_ID is only a manual override
 * lever (set it in apphosting.yaml) if a build ever ships without the id.
 *
 * This property is intentionally SEPARATE from the marketing site's GA4
 * (G-1V62CJELWQ / GTM-TVLLZ2N). The app is embedded on kasselmansolar.com as a
 * cross-origin iframe, so the marketing tags never see these events and these
 * events never reach the marketing property — no double-counting either way.
 *
 * Resilient by design: never throws, never blocks the UI. No-ops when there is
 * no measurement id (local dev, or a non-PROD build) or when the environment
 * doesn't support Analytics (e.g. cookies disabled).
 */

/** Standalone (opened directly) vs. embedded (iframe on the marketing site). */
const context: "embed" | "standalone" =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("embed")
    ? "embed"
    : "standalone";

function measurementId(): string | undefined {
  return getFirebaseConfig()?.measurementId ?? import.meta.env.VITE_GA_MEASUREMENT_ID ?? undefined;
}

let initPromise: Promise<Analytics | null> | null = null;

/** Idempotent. Returns the Analytics instance, or null when unavailable. */
export function initAnalytics(): Promise<Analytics | null> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    // Only real production builds with a measurement id emit analytics; keeps
    // dev/mock traffic out of the property.
    if (!import.meta.env.PROD || !measurementId()) return null;
    try {
      if (!(await isSupported())) return null;
      return getAnalytics(getFirebaseApp());
    } catch {
      return null;
    }
  })();
  return initPromise;
}

type Primitive = string | number | boolean;

/** A GA4 ecommerce `items` entry (select_item, view_item_list, begin_checkout…). */
export interface AnalyticsItem {
  item_id?: string;
  item_name?: string;
  item_category?: string;
  price?: number;
  quantity?: number;
}

type EventParams = Record<string, Primitive | AnalyticsItem[]>;

type Gtag = (command: string, targetId: string, field: string, cb: (value: unknown) => void) => void;

/** How long to wait for a single gtag `get` callback before giving up (#40). */
const GA_ID_TIMEOUT_MS = 800;

const EMPTY_IDS: SubmissionAnalytics = { clientId: null, sessionId: null };

/**
 * Capture the GA4 client_id + session_id for this browser (#40), so n8n can fire
 * the server-side `purchase` against the same GA user once the contract is
 * signed. Resilient by contract: never throws, always resolves, and bounds each
 * gtag lookup so it can't stall the recommend call. Returns nulls whenever
 * analytics is unavailable (dev/mock, cookies off).
 *
 * Deps are injectable for tests; production calls it with no args, using the
 * Firebase-initialized analytics and the global gtag the SDK installs.
 */
export async function getGaIds(deps?: {
  ready?: () => Promise<unknown>;
  id?: () => string | undefined;
  gtag?: Gtag;
}): Promise<SubmissionAnalytics> {
  const ready = deps?.ready ?? initAnalytics;
  const getId = deps?.id ?? measurementId;
  const gtag =
    deps?.gtag ??
    (typeof window !== "undefined" ? (window as unknown as { gtag?: Gtag }).gtag : undefined);

  const analytics = await ready();
  const id = getId();
  if (!analytics || !id || typeof gtag !== "function") return EMPTY_IDS;

  const read = (field: string): Promise<string | null> =>
    new Promise((resolve) => {
      let settled = false;
      const settle = (v: string | null) => {
        if (!settled) {
          settled = true;
          resolve(v);
        }
      };
      // gtag `get` can silently never call back — bound every lookup.
      setTimeout(() => settle(null), GA_ID_TIMEOUT_MS);
      try {
        gtag("get", id, field, (value: unknown) =>
          settle(value === undefined || value === null ? null : String(value)),
        );
      } catch {
        settle(null);
      }
    });

  const [clientId, sessionId] = await Promise.all([read("client_id"), read("session_id")]);
  return { clientId, sessionId };
}

/** Fire-and-forget custom event. Always tagged with the render context. */
export function track(event: string, params: EventParams = {}): void {
  void initAnalytics()
    .then((analytics) => {
      if (analytics) logEvent(analytics, event, { context, ...params });
    })
    .catch(() => {
      /* analytics must never break the funnel */
    });
}
